import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth, useClerk } from "@clerk/clerk-react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Dashboard from "./pages/Dashboard";
import ManageStudents from "./pages/ManageStudents";
import ManageStaff from "./pages/ManageStaff";
import Certificates from "./pages/Certificates";
import Settings from "./pages/Settings";
import SignIn from "./pages/SignIn";
import InviteAccept from "./pages/InviteAccept";
import { ProtectedRoute } from "./components/ProtectedRoute";
import Sidebar from "./components/layout/Sidebar";
import IdleLogoutWatcher from "./components/IdleLogoutWatcher";
import EnvironmentBanner from "./components/EnvironmentBanner";
import { setAccessTokenGetter, setSignOutFunction } from "./services/apiFetch";

/** Bridges Clerk's auth hooks into the module-level apiFetch utility. */
function AuthTokenSetter() {
  const { getToken } = useAuth();
  const { signOut } = useClerk();
  useEffect(() => {
    setAccessTokenGetter(() => getToken());
    setSignOutFunction((opts) => signOut(opts));
  }, [getToken, signOut]);
  return null;
}

const INVITE_TOKEN_KEY = "hypebook.schoolAdmin.inviteToken";

/**
 * App-wide invite resumption. Clerk's sign-up flow (email verification,
 * Turnstile, sometimes a fresh tab) does not reliably return the user to
 * /invite/:token — it lands them on signInFallbackRedirectUrl
 * (/dashboard), where a freshly-provisioned STUDENT 403s and gets
 * bounced before accept() ever runs. The invite token is persisted in
 * localStorage (survives across tabs, unlike sessionStorage); once the user
 * is authenticated this guard routes them to /invite/:token from wherever
 * Clerk dropped them, so the accept call actually fires.
 */
function InviteContinuation() {
  const { isLoaded, isSignedIn } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (!isLoaded || !isSignedIn) return;
    if (location.pathname.startsWith("/invite/")) return;
    const token = localStorage.getItem(INVITE_TOKEN_KEY);
    if (token) navigate(`/invite/${token}`, { replace: true });
  }, [isLoaded, isSignedIn, location.pathname, navigate]);
  return null;
}

function App() {
  return (
    <BrowserRouter>
      <AuthTokenSetter />
      <InviteContinuation />
      <IdleLogoutWatcher />
      {/* Renders nothing on production; badges every other host. */}
      <EnvironmentBanner />
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      <Routes>
        {/* Wildcard so Clerk's <SignIn> can own its own subpaths (OAuth
            SSO callback at /signin/sso-callback, sign-up at /signin/sign-up,
            etc.). */}
        <Route path="/signin/*" element={<SignIn />} />
        {/* Compat: students-app-style /auth/signin path → redirect */}
        <Route path="/auth/signin" element={<Navigate to="/signin" replace />} />
        {/* Compat: legacy Logto OAuth callback → just send to /signin (Clerk
            handles its own callback inline; nothing should be hitting this
            path post-migration but keep the redirect so old bookmarks
            work). */}
        <Route path="/callback" element={<Navigate to="/signin" replace />} />
        <Route path="/invite/:token" element={<InviteAccept />} />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <div className="flex min-h-screen bg-gray-50">
                <Sidebar />
                <div className="flex-1 ml-[230px]">
                  <Dashboard />
                </div>
              </div>
            </ProtectedRoute>
          }
        />
        <Route
          path="/manage-students"
          element={
            <ProtectedRoute>
              <div className="flex min-h-screen bg-gray-50">
                <Sidebar />
                <div className="flex-1 ml-[230px]">
                  <ManageStudents />
                </div>
              </div>
            </ProtectedRoute>
          }
        />
        <Route
          path="/manage-staff"
          element={
            <ProtectedRoute>
              <div className="flex min-h-screen bg-gray-50">
                <Sidebar />
                <div className="flex-1 ml-[230px]">
                  <ManageStaff />
                </div>
              </div>
            </ProtectedRoute>
          }
        />
        {/* Engagement is now a tab under Manage Students; keep the old route as
            a redirect so bookmarks / dashboard deep-links still land there. */}
        <Route path="/engagement" element={<Navigate to="/manage-students?tab=engagement" replace />} />
        <Route
          path="/certificates"
          element={
            <ProtectedRoute>
              <div className="flex min-h-screen bg-gray-50">
                <Sidebar />
                <div className="flex-1 ml-[230px]">
                  <Certificates />
                </div>
              </div>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <div className="flex min-h-screen bg-gray-50">
                <Sidebar />
                <div className="flex-1 ml-[230px]">
                  <Settings />
                </div>
              </div>
            </ProtectedRoute>
          }
        />
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        {/* Catch-all → send unknown paths to the sign-in surface */}
        <Route path="*" element={<Navigate to="/signin" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
